import React from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Linking,
  Platform,
} from "react-native";
import { platformAlert, showAlert } from "../utils/platformAlert";
import { Image } from "expo-image";
import { useRoute, RouteProp, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { Feather, Ionicons } from "@expo/vector-icons";
import { TextInput } from "react-native";
import { format } from "date-fns";
import Constants from "expo-constants";

import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import type { RootStackParamList } from "../types/navigation";
import { DeliveryTracker } from "../components/DeliveryTracker";
import { InAppBrowserModal } from "../components/ui/InAppBrowserModal";
import { supabase } from "../lib/supabase";
import { DELIVERY_CONFIG, DeliveryHelpers } from "../config/deliveryConfig";
import { usePostHog } from "../hooks/usePostHog";
import { trackEvent } from "../services/posthog";
import { notificationService } from "../services/NotificationService";
import { formatPrice } from "../utils/format";
import { findOrCreateChat } from "../utils/chatUtils";
import { useAuth } from "../hooks/useAuth";
import { logger } from "../utils/logger";
import { orderEvents } from "../utils/orderEvents";
// если у тебя уже есть тип Order из useOrders — можно импортнуть:
// import type { Order } from "../hooks/useOrders";

/** Минимальный тип заказа для экрана покупателя */
export type BuyerOrder = {
  id: string;
  created_at: string;
  status?: string | null; // общий статус
  delivery_status?: string | null; // статус доставки (если ведёшь отдельно)
  delivery_id?: string | null; // ID доставки для трекинга
  total_amount?: number | null;
  delivery_fee?: number | null;
  tracking_url?: string | null;
  payment_status?: string | null;
  payment_method?: string | null;
  payer_email?: string | null;
  payment_intent_id?: string | null;
  receipt_url?: string | null;

  /** items может быть как нормализованным массивом,
   *  так и "сырым" слепком корзины */
  items: any[];

  /** произвольные детали (breakdown, deliveries и т.п.) */
  payment_details?: {
    breakdown?: {
      items_subtotal?: number;
      delivery_total?: number;
      platform_fee?: number;
    };
    vendor_deliveries?: Record<
      string,
      { delivery_id: string; tracking_url: string; fee: number }
    >;
    cart_snapshot?: any[];
    stripe?: Record<string, any>;
  } | null;
};

type Nav = NativeStackNavigationProp<RootStackParamList>;
type RouteP = RouteProp<{ params: { order: BuyerOrder } }, "params">;

// ───────────────────────────────── helpers ─────────────────────────────────
function toDisplayLine(line: any) {
  // нормализованный вид из заказа
  if (line?.title || line?.name || line?.unit_price || line?.image) {
    return {
      title: line.title ?? line.name ?? line.product?.title ?? "Unknown Product",
      qty: Number(line.quantity ?? 1),
      price: Number(line.unit_price ?? line.price ?? 0),
      image: line.image ?? null,
      vendorName: line.vendor_name ?? null,
      vendorId: line.vendor_id ?? null,
      vendorUserId: line.vendor_user_id ?? null,
    };
  }
  // сырой слепок из корзины
  const p = line?.product ?? {};
  return {
    title: p.title ?? p.name ?? "Unknown Product",
    qty: Number(line?.quantity ?? 1),
    price: Number(p.price ?? 0),
    image: Array.isArray(p.images) && p.images.length ? p.images[0] : null,
    vendorName: p.vendor?.store_name ?? null,
    vendorId: p.vendor?.id ?? p.vendor_id ?? null,
    vendorUserId: p.vendor?.user_id ?? null,
  };
}

const money = (n?: number | null) =>
  typeof n === "number" && !Number.isNaN(n) ? formatPrice(n) : "—";


const statusText = (status?: string | null) => {
  switch (status) {
    case "pending":
      return "Pending Approval";
    case "confirmed":
      return "Order Confirmed";
    case "paid":
    case "succeeded":
      return "Waiting for Seller";
    case "wrapping_up":
      return "Wrapping Up";
    case "packed":
      return "Packed";
    case "pickup_pending":
      return "Courier on the Way";
    case "pickup_complete":
      return "Pickup Complete";
    case "in_transit":
      return "In Transit";
    case "delivered":
      return "Delivered";
    case "completed":
      return "Completed";
    case "rejected":
      return "Rejected";
    case "cancelled":
      return "Cancelled";
    case "failed":
      return "Failed";
    default:
      // Convert snake_case to Title Case for unknown statuses
      if (status) {
        return status
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
      }
      return "—";
  }
};

// Jade green used throughout app for status/success states
const JADE_GREEN = "#00A86B";
const ROSE_RED = "#F43F5E";

const statusColor = (status?: string | null) => {
  switch (status) {
    case "pending":
      return "#f59e0b"; // amber for pending
    case "confirmed":
    case "paid":
    case "succeeded":
    case "wrapping_up":
    case "packed":
    case "pickup_pending":
    case "pickup_complete":
    case "in_transit":
    case "delivered":
    case "completed":
      return JADE_GREEN;
    case "rejected":
    case "cancelled":
    case "failed":
      return ROSE_RED;
    default:
      return "#6b7280";
  }
};

// ─────────────────────────────── component ────────────────────────────────
export default function BuyerOrderDetailsScreen() {
  const colors = useColors();
  const s = createStyles(colors);
  const navigation = useNavigation<Nav>();
  const route = useRoute<RouteP>();
  const initialOrder = route.params.order as BuyerOrder;
  const [order, setOrder] = React.useState<BuyerOrder>(initialOrder);
  const [saving, setSaving] = React.useState(false);
  const posthog = usePostHog();
  const { user } = useAuth();
  const [startingChatForVendor, setStartingChatForVendor] = React.useState<string | null>(null);
  const [showTrackingModal, setShowTrackingModal] = React.useState(false);
  
  // Rating state: { [productId]: { rating: number, feedback: string, submitted: boolean } }
  const [ratings, setRatings] = React.useState<Record<string, { rating: number; feedback: string; submitted: boolean }>>({});
  const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);
  
  // Get current user ID on mount
  React.useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setCurrentUserId(user.id);
    };
    getCurrentUser();
  }, []);
  
  // Load existing ratings for this order's products (user can update their rating on repeat purchases)
  React.useEffect(() => {
    const loadExistingRatings = async () => {
      if (!currentUserId || !order?.items?.length) return;
      
      // Get product IDs from order items
      const productIds = order.items
        .map(item => item.product_id || item.product?.id)
        .filter(Boolean);
      
      if (productIds.length === 0) return;
      
      // Get user's existing ratings for these products (one rating per product per user)
      const { data: existingRatings } = await supabase
        .from('product_ratings')
        .select('product_id, rating, feedback')
        .eq('user_id', currentUserId)
        .in('product_id', productIds);
      
      if (existingRatings?.length) {
        const ratingsMap: Record<string, { rating: number; feedback: string; submitted: boolean }> = {};
        existingRatings.forEach(r => {
          ratingsMap[r.product_id] = { 
            rating: r.rating, 
            feedback: r.feedback || '', 
            submitted: true 
          };
        });
        setRatings(prev => ({ ...prev, ...ratingsMap }));
      }
    };
    
    loadExistingRatings();
  }, [currentUserId, order?.items]);
  // Use Constants for runtime access or fallback to process.env for build-time
  const ENABLE_CANCEL_DELIVERY =
    Constants?.expoConfig?.extra?.EXPO_PUBLIC_ENABLE_CANCEL_DELIVERY === "true" ||
    process.env.EXPO_PUBLIC_ENABLE_CANCEL_DELIVERY === "true" ||
    true; // Default to true since we enabled it in eas.json

  console.log("Order details render", order);
  console.log("ENABLE_CANCEL_DELIVERY:", ENABLE_CANCEL_DELIVERY, "from Constants:", Constants?.expoConfig?.extra?.EXPO_PUBLIC_ENABLE_CANCEL_DELIVERY, "from process.env:", process.env.EXPO_PUBLIC_ENABLE_CANCEL_DELIVERY);

  // Poll order status to get latest delivery updates
  React.useEffect(() => {
    if (!order?.id) return;

    const fetchLatestOrder = async () => {
      try {
        const { data, error } = await supabase
          .from('orders')
          .select('*')
          .eq('id', order.id)
          .single();

        if (error) {
          console.error('[BuyerOrderDetails] Error fetching order:', error);
          return;
        }

        if (data) {
          const currentStatus = order.delivery_status || order.status;
          const newStatus = data.delivery_status || data.status;
          
          if (currentStatus !== newStatus) {
            console.log('[BuyerOrderDetails] Order status updated:', {
              orderId: order.id,
              oldStatus: currentStatus,
              newStatus: newStatus
            });
            setOrder(data as BuyerOrder);
          }
        }
      } catch (err) {
        console.error('[BuyerOrderDetails] Error in fetchLatestOrder:', err);
      }
    };

    // Initial fetch
    fetchLatestOrder();

    // Poll every 5 seconds for active deliveries (continue polling until completed/cancelled)
    const currentStatus = order.delivery_status || order.status || '';
    const isActiveDelivery = order.delivery_id && 
      !['completed', 'cancelled', 'rejected'].includes(currentStatus);
    
    if (isActiveDelivery) {
      console.log('[BuyerOrderDetails] Starting polling for active delivery:', {
        orderId: order.id,
        deliveryId: order.delivery_id,
        currentStatus
      });
      const pollInterval = setInterval(fetchLatestOrder, 5000);
      return () => {
        console.log('[BuyerOrderDetails] Stopping polling');
        clearInterval(pollInterval);
      };
    } else {
      console.log('[BuyerOrderDetails] Not polling - delivery is final:', currentStatus);
    }
  }, [order?.id, order?.delivery_id, order?.delivery_status, order?.status]);

  // Real-time subscription for order updates
  React.useEffect(() => {
    if (!order?.id) return;

    const channel = supabase
      .channel(`order-${order.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${order.id}`,
        },
        (payload) => {
          console.log('[BuyerOrderDetails] Order updated via realtime:', payload.new);
          setOrder(payload.new as BuyerOrder);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [order?.id]);

  // Poll delivery status from Uber API if delivery_id exists
  React.useEffect(() => {
    if (!order?.delivery_id) return;

    const pollDeliveryStatus = async () => {
      try {
        // Call our edge function to get latest delivery status from Uber
        const { data, error } = await supabase.functions.invoke('uber-delivery', {
          body: { delivery_id: order.delivery_id }
        });

        if (error) {
          console.error('[BuyerOrderDetails] Error polling delivery status:', error);
          return;
        }

        if (data?.uber_status?.status) {
          const uberStatus = data.uber_status.status;
          console.log('[BuyerOrderDetails] Delivery status from Uber:', uberStatus);
          
          // Map Uber status to our internal status
          let targetStatus: string | null = null;
          if (uberStatus === 'delivered') {
            targetStatus = 'completed';
          } else if (uberStatus === 'pickup_complete') {
            targetStatus = 'in_transit';
          } else if (uberStatus === 'dropoff') {
            targetStatus = 'pickup_complete';
          } else if (uberStatus === 'pickup' || uberStatus === 'shopping_completed') {
            targetStatus = 'pickup_pending';
          }
          
          // Check current status in database
          const currentStatus = order.delivery_status || order.status;
          
          // Update database if Uber status indicates a change
          if (targetStatus && currentStatus !== targetStatus) {
            console.log(`[BuyerOrderDetails] Uber status "${uberStatus}" maps to "${targetStatus}" but DB shows "${currentStatus}" - Updating database...`);
            
            // Update the order status in the database
            const { data: updatedOrder, error: updateError } = await supabase
              .from('orders')
              .update({ 
                status: targetStatus,
                updated_at: new Date().toISOString()
              })
              .eq('id', order.id)
              .select()
              .single();
            
            if (updateError) {
              console.error('[BuyerOrderDetails] Error updating order status:', updateError);
            } else if (updatedOrder) {
              console.log('[BuyerOrderDetails] Order status updated to completed:', updatedOrder.id);
              setOrder(updatedOrder as BuyerOrder);

              // Track delivery completion when status reaches completed
              try {
                trackEvent(
                  "delivery_completed",
                  {
                    order_id: updatedOrder.id,
                    delivery_id: updatedOrder.delivery_id,
                    source: "uber_poll",
                    status: updatedOrder.status || updatedOrder.delivery_status,
                  },
                  posthog || undefined
                );
              } catch (analyticsError) {
                console.warn(
                  "[BuyerOrderDetails] Failed to track delivery_completed (uber_poll):",
                  analyticsError
                );
              }
            }
          } else if (targetStatus && currentStatus !== targetStatus) {
            // For other status changes, refresh from DB (webhook should have updated it)
            console.log('[BuyerOrderDetails] Status mismatch - refreshing from DB:', {
              uberStatus,
              targetStatus,
              currentStatus
            });
            
            const { data: refreshedOrder } = await supabase
              .from('orders')
              .select('*')
              .eq('id', order.id)
              .single();
            
            if (refreshedOrder) {
              setOrder(refreshedOrder as BuyerOrder);
            }
          }
        }
      } catch (err) {
        console.error('[BuyerOrderDetails] Error in pollDeliveryStatus:', err);
      }
    };

    // Poll for all non-final delivery statuses
    const currentStatus = order.delivery_status || order.status || '';
    const isActiveDelivery = !['completed', 'cancelled', 'rejected', 'delivered'].includes(currentStatus);

    if (isActiveDelivery) {
      console.log('[BuyerOrderDetails] Starting Uber API polling for delivery:', {
        orderId: order.id,
        deliveryId: order.delivery_id,
        currentStatus
      });
      // Poll every 10 seconds
      const pollInterval = setInterval(pollDeliveryStatus, 10000);
      return () => {
        console.log('[BuyerOrderDetails] Stopping Uber API polling');
        clearInterval(pollInterval);
      };
    } else {
      console.log('[BuyerOrderDetails] Not polling Uber API - delivery is final:', currentStatus);
    }
  }, [order?.delivery_id, order?.delivery_status, order?.status, order?.id]);

  // Submit rating for a product
  const submitRating = async (productId: string) => {
    if (!currentUserId) {
      Alert.alert('Error', 'You must be logged in to rate products');
      return;
    }
    
    const ratingData = ratings[productId];
    if (!ratingData?.rating || ratingData.rating < 1) {
      Alert.alert('Error', 'Please select a rating');
      return;
    }
    
    try {
      setSaving(true);
      
      // Get product info and vendor's user_id for notification
      const { data: product } = await supabase
        .from('products')
        .select(`
          title, 
          vendor_id,
          vendors:vendor_id (
            user_id
          )
        `)
        .eq('id', productId)
        .single();
      
      // Check if user already has a rating for this product
      const { data: existing } = await supabase
        .from('product_ratings')
        .select('id')
        .eq('product_id', productId)
        .eq('user_id', currentUserId)
        .maybeSingle();
      
      const isNewRating = !existing;
      
      if (existing) {
        // Update existing rating (user can update their review on repeat purchases)
        const { error } = await supabase
          .from('product_ratings')
          .update({
            rating: ratingData.rating,
            feedback: ratingData.feedback || null,
          })
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        // Insert new rating
        const { error } = await supabase
          .from('product_ratings')
          .insert({
            product_id: productId,
            user_id: currentUserId,
            rating: ratingData.rating,
            feedback: ratingData.feedback || null,
          });
        
        if (error) throw error;
      }
      
      // Mark as submitted
      setRatings(prev => ({
        ...prev,
        [productId]: { ...prev[productId], submitted: true }
      }));
      
      // Send notification to seller (only for new ratings, not updates)
      // Use vendor's user_id (not vendor_id) since push tokens are tied to auth users
      const sellerUserId = (product?.vendors as any)?.user_id;
      if (isNewRating && sellerUserId) {
        try {
          // Look up the vendor_order_id for this order and vendor
          let vendorOrderId: string | null = null;
          if (order?.id && product?.vendor_id) {
            const { data: vendorOrder } = await supabase
              .from('vendor_orders')
              .select('id')
              .eq('parent_order_id', order.id)
              .eq('vendor_id', product.vendor_id)
              .maybeSingle();
            vendorOrderId = vendorOrder?.id || null;
          }
          
          console.log('[Rating] Sending notification to seller:', {
            sellerUserId,
            vendorId: product.vendor_id,
            vendorOrderId,
            productTitle: product.title,
            rating: ratingData.rating,
            feedback: ratingData.feedback,
          });
          
          await notificationService.sendNewReviewNotification(
            sellerUserId,
            product.title || 'Product',
            ratingData.rating,
            ratingData.feedback || null,
            vendorOrderId
          );
          console.log('[Rating] Notification sent to seller successfully');
        } catch (notifError) {
          console.warn('[Rating] Failed to send notification to seller:', notifError);
          // Don't fail the rating submission if notification fails
        }
      }
      
      Alert.alert('Thank you!', 'Your rating has been submitted.');
    } catch (e: any) {
      console.error('Error submitting rating:', e);
      Alert.alert('Error', e?.message || 'Failed to submit rating');
    } finally {
      setSaving(false);
    }
  };

  const cancelOrder = async () => {
    if (!order) return;

    // Check if this is a paid order that needs refund
    const needsRefund = ["paid", "wrapping_up", "packed"].includes(order.status);
    const confirmMessage = needsRefund
      ? "Are you sure you want to cancel this order? You will be refunded. This action cannot be undone."
      : "Are you sure you want to cancel this order? This action cannot be undone.";

    platformAlert(
      "Cancel Order",
      confirmMessage,
      [
        { text: "Keep Order", style: "cancel" },
        {
          text: "Cancel Order",
          style: "destructive",
          onPress: async () => {
            try {
              setSaving(true);
              
              console.log("[BuyerOrderDetails] Cancelling order via edge function:", order.id);

              // Call the buyer-cancel-order edge function which handles:
              // 1. Restoring inventory (with service role privileges)
              // 2. Processing refund
              // 3. Updating order and vendor_orders status
              // 4. Cancelling pending disbursements
              // 5. Sending notifications to vendors
              const { data: result, error } = await supabase.functions.invoke(
                "buyer-cancel-order",
                {
                  body: {
                    order_id: order.id,
                    reason: "cancelled_by_buyer",
                  },
                }
              );

              if (error) {
                console.error("[BuyerOrderDetails] Edge function error:", error);
                throw error;
              }

              if (result?.error) {
                console.error("[BuyerOrderDetails] Cancellation failed:", result.error);
                throw new Error(result.error);
              }

              console.log("[BuyerOrderDetails] Order cancelled successfully:", result);

              // Show the message from the edge function or a default message
              const successMessage = result?.message || 
                (needsRefund
                  ? "Your order has been cancelled. Your refund is being processed."
                  : "Your order has been cancelled successfully.");
              
              // Emit order event to notify other components (like OngoingOrderDisplay)
              orderEvents.emit();
              
              showAlert("Order Cancelled", successMessage);
              navigation.goBack();
            } catch (e: any) {
              showAlert("Error", e?.message || "Failed to cancel the order");
            } finally {
              setSaving(false);
            }
          },
        },
      ]
    );
  };

  const cancelDelivery = async () => {
    if (!order || !order.delivery_id) return;

    platformAlert(
      "Cancel Delivery",
      "Are you sure you want to cancel the delivery? This will stop the courier, cancel the order, and you will be refunded.",
      [
        { text: "Keep Delivery", style: "cancel" },
        {
          text: "Cancel Delivery",
          style: "destructive",
          onPress: async () => {
            try {
              setSaving(true);

              // Call the uber-cancel-delivery function which handles:
              // 1. Cancelling with Uber
              // 2. Updating order status
              // 3. Restoring inventory
              // 4. Processing refund
              // 5. Sending notification to buyer
              console.log("[BuyerOrderDetails] 🚀 Calling uber-cancel-delivery...");
              console.log("[BuyerOrderDetails] delivery_id:", order.delivery_id);
              console.log("[BuyerOrderDetails] parent_order_id:", order.id);
              
              const { data, error } = await supabase.functions.invoke(
                "uber-cancel-delivery",
                {
                  body: {
                    delivery_id: order.delivery_id,
                    reason: "cancelled_by_customer",
                    parent_order_id: order.id,
                  },
                }
              );

              console.log("[BuyerOrderDetails] Response data:", JSON.stringify(data, null, 2));
              console.log("[BuyerOrderDetails] Response error:", error);

              if (error) throw error;

              console.log("[BuyerOrderDetails] ✅ Delivery cancelled successfully:", data);

              // Show appropriate message based on refund status
              const refundMessage = data?.refund?.processed 
                ? "Your delivery has been cancelled and you have been refunded. The refund will appear in your account within 5-10 business days."
                : "Your delivery has been cancelled. Your refund is being processed and will appear in your account within 5-10 business days.";
              
              // Emit order event to notify other components (like OngoingOrderDisplay)
              orderEvents.emit();
              
              showAlert("Delivery Cancelled", refundMessage);
              navigation.goBack();
            } catch (e: any) {
              showAlert(
                "Error",
                e?.message || "Failed to cancel the delivery"
              );
            } finally {
              setSaving(false);
            }
          },
        },
      ]
    );
  };

  const startChatWithVendor = async (vendorId: string | null, vendorName: string) => {
    if (!user) {
      platformAlert("Error", "Please sign in to message the seller.");
      return;
    }

    const chatKey = vendorId || vendorName;
    setStartingChatForVendor(chatKey);

    try {
      let targetVendorId = vendorId;
      let targetUserId: string | null = null;

      // If we have vendorId, fetch user_id directly
      if (vendorId) {
        const { data: vendor, error: vendorError } = await supabase
          .from("vendors")
          .select("user_id")
          .eq("id", vendorId)
          .single();

        if (!vendorError && vendor?.user_id) {
          targetUserId = vendor.user_id;
        }
      }
      
      // If we don't have vendorId or couldn't find user_id, try to find vendor by name
      if (!targetUserId && vendorName) {
        const { data: vendor, error: vendorError } = await supabase
          .from("vendors")
          .select("id, user_id")
          .eq("store_name", vendorName)
          .single();

        if (!vendorError && vendor?.user_id) {
          targetVendorId = vendor.id;
          targetUserId = vendor.user_id;
        }
      }

      if (!targetUserId || !targetVendorId) {
        throw new Error("Could not find seller information");
      }

      logger.debug('Chat', `Starting chat between user ${user.id} and vendor user ${targetUserId} (vendor ${targetVendorId})`);

      const chatId = await findOrCreateChat(user.id, targetUserId, targetVendorId);
      
      logger.debug('Chat', `Navigating to chat: ${chatId}`);
      (navigation as any).navigate("Chat", { id: chatId });
      
    } catch (error) {
      logger.error('Chat', `Error starting chat:`, error);
      platformAlert("Error", "Unable to start chat. Please try again.");
    } finally {
      setStartingChatForVendor(null);
    }
  };

  if (!order) {
    return (
      <View style={s.center}>
        <ActivityIndicator color={colors.primary} />
        <Text style={s.muted}>Loading order…</Text>
      </View>
    );
  }

  const created = order.created_at ? new Date(order.created_at) : new Date();
  const lines = Array.isArray(order.items)
    ? order.items.map(toDisplayLine)
    : [];

  const breakdown = order.payment_details?.breakdown || {};
  const subtotal =
    typeof breakdown.items_subtotal === "number"
      ? breakdown.items_subtotal
      : undefined;
  const delivery =
    typeof order.delivery_fee === "number"
      ? order.delivery_fee
      : typeof breakdown.delivery_total === "number"
        ? breakdown.delivery_total
        : undefined;
  const platformFee =
    typeof breakdown.platform_fee === "number"
      ? breakdown.platform_fee
      : undefined;
const deliveryForFee =
  typeof delivery === "number" ? delivery : 0;
const computedBuyerFee =
  typeof subtotal === "number"
    ? DeliveryHelpers.calculateBuyerFee(subtotal, deliveryForFee)
    : undefined;

  const canTrack =
    !!order.tracking_url &&
    ["pickup_pending", "in_transit", "pickup_complete", "delivered", "completed"].includes(
      order.delivery_status || order.status || ""
    );

  // Cancel button logic: show for statuses BEFORE vendor marks as packed
  // Once packed, delivery is automatically started and cancellation would incur fees
  const canCancel = ["pending", "confirmed", "paid", "wrapping_up"].includes(
    order.delivery_status || order.status || ""
  );

  // Cancel delivery button logic: DISABLED after packed status
  // Once delivery is dispatched (pickup_pending and beyond), cancellation is not allowed
  // to avoid disrupting the courier and incurring cancellation fees
  const canCancelDelivery = false;

  // Rating logic: show for delivered/completed orders
  const canRate = ["delivered", "completed"].includes(
    order.delivery_status || order.status || ""
  );

  // Helper to render star rating selector
  const renderStarSelector = (productId: string) => {
    const currentRating = ratings[productId]?.rating || 0;
    const isSubmitted = ratings[productId]?.submitted || false;
    
    return (
      <View style={s.starRow}>
        {[1, 2, 3, 4, 5].map((star) => (
          <TouchableOpacity
            key={star}
            onPress={() => {
              if (!isSubmitted) {
                setRatings(prev => ({
                  ...prev,
                  [productId]: { 
                    ...prev[productId], 
                    rating: star,
                    feedback: prev[productId]?.feedback || '',
                    submitted: false 
                  }
                }));
              }
            }}
            disabled={isSubmitted}
          >
            <Ionicons
              name={star <= currentRating ? "star" : "star-outline"}
              size={28}
              color={star <= currentRating ? colors.primary : colors.textLight}
            />
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  return (
    <>
    <ScrollView style={s.container} contentInsetAdjustmentBehavior="automatic">
      {/* Header */}
      <View style={s.headerCard}>
        <View style={s.headerRow}>
          <Text style={s.title}>Order #{String(order.id).slice(0, 8)}</Text>
          <View style={[s.statusChip, { backgroundColor: statusColor(order.delivery_status || order.status) }]}>
            <Feather
              name={(() => {
                const status = order.delivery_status || order.status;
                switch (status) {
                  case "pending": return "clock";
                  case "confirmed": return "check-circle";
                  case "paid":
                  case "succeeded": return "check-circle";
                  case "wrapping_up": return "package";
                  case "packed": return "package";
                  case "pickup_pending": return "truck";
                  case "pickup_complete": return "check-circle";
                  case "in_transit": return "truck";
                  case "delivered":
                  case "completed": return "check-circle";
                  case "cancelled":
                  case "rejected":
                  case "failed": return "x-circle";
                  default: return "help-circle";
                }
              })() as any}
              size={14}
              color="#000000"
            />
            <Text style={s.statusChipText}>
              {statusText(order.delivery_status || order.status)}
            </Text>
          </View>
        </View>

        <Text style={s.muted}>
          Created {format(created, "MMM d, yyyy, h:mm a")}
        </Text>
      </View>
      {/* Items */}
      <View style={s.card}>
        <Text style={s.sectionTitle}>Items</Text>
        {lines.map((l, idx) => (
          <View key={`${order.id}-line-${idx}`} style={s.itemRow}>
            <View style={s.itemLeft}>
              {l.image ? (
                <Image 
                  source={{ uri: l.image }} 
                  style={s.itemThumb}
                  contentFit="cover"
                  cachePolicy="memory-disk"
                  transition={0}
                />
              ) : (
                <View style={s.thumbPlaceholder}>
                  <Feather name="image" size={18} color="#9CA3AF" />
                </View>
              )}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.itemName} numberOfLines={2}>
                {l.title}
              </Text>
              <Text style={s.itemMeta}>
                {l.qty}× {money(l.price)}
              </Text>
              {!!l.vendorName && (
                <TouchableOpacity
                  onPress={() => {
                    if (l.vendorId) {
                      (navigation as any).navigate("Vendor", { id: l.vendorId });
                    }
                  }}
                  disabled={!l.vendorId}
                >
                  <Text style={s.vendorNameLink}>
                    {l.vendorName}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        ))}
      </View>

      {/* Summary */}
      <View style={s.card}>
        <Text style={s.sectionTitle}>Summary</Text>
        {typeof subtotal === "number" && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Items</Text>
            <Text style={s.value}>{money(subtotal)}</Text>
          </View>
        )}
        {typeof platformFee === "number" && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Processing Fee </Text>
            <Text style={s.value}>{money(platformFee)}</Text>
          </View>
        )}
        {typeof subtotal === "number" && !platformFee && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Processing Fee</Text>
            <Text style={s.value}>{money(DeliveryHelpers.calculateBuyerFee(subtotal, delivery ?? 0))}</Text>
          </View>
        )}
        {typeof delivery === "number" && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Delivery</Text>
            <Text style={s.value}>{money(delivery)}</Text>
          </View>
        )}
        <View style={[s.rowBetween, { marginTop: 8 }]}>
          <Text style={s.totalLabel}>Total</Text>
          <Text style={s.totalValue}>{money(order.total_amount ?? 0)}</Text>
        </View>
      </View>

      {/* Rate Your Purchase - only show for delivered/completed orders */}
      {canRate && (
        <View style={s.card}>
          <Text style={s.sectionTitle}>Rate Your Purchase</Text>
          <Text style={[s.muted, { marginBottom: spacing.sm }]}>
            How was your experience with this order?
          </Text>
          
          {order.items.map((item, idx) => {
            const productId = item.product_id || item.product?.id;
            const productTitle = item.title || item.product?.title || 'Item';
            const productImage = item.image || (item.product?.images?.[0]);
            const isSubmitted = ratings[productId]?.submitted || false;
            
            if (!productId) return null;
            
            return (
              <View key={`rate-${productId}-${idx}`} style={s.ratingItemContainer}>
                <View style={s.ratingItemHeader}>
                  {productImage ? (
                    <Image 
                      source={{ uri: productImage }} 
                      style={s.ratingItemThumb}
                      contentFit="cover"
                    />
                  ) : (
                    <View style={[s.ratingItemThumb, s.thumbPlaceholder]}>
                      <Feather name="image" size={16} color="#9CA3AF" />
                    </View>
                  )}
                  <Text style={s.ratingItemTitle} numberOfLines={2}>{productTitle}</Text>
                </View>
                
                {renderStarSelector(productId)}
                
                {!isSubmitted && (
                  <TextInput
                    style={s.feedbackInput}
                    placeholder="Add feedback (optional)"
                    placeholderTextColor={colors.textLight}
                    value={ratings[productId]?.feedback || ''}
                    onChangeText={(text) => {
                      if (text.length <= 200) {
                        setRatings(prev => ({
                          ...prev,
                          [productId]: { 
                            ...prev[productId], 
                            feedback: text,
                            rating: prev[productId]?.rating || 0,
                            submitted: false 
                          }
                        }));
                      }
                    }}
                    multiline
                    maxLength={200}
                  />
                )}
                
                {isSubmitted ? (
                  <View>
                    {ratings[productId]?.feedback ? (
                      <Text style={s.submittedFeedback}>"{ratings[productId].feedback}"</Text>
                    ) : null}
                    <View style={s.submittedRow}>
                      <View style={s.submittedBadge}>
                        <Ionicons name="checkmark-circle" size={16} color={colors.primary} />
                        <Text style={s.submittedText}>Rating submitted</Text>
                      </View>
                      <TouchableOpacity
                        style={s.editButton}
                        onPress={() => {
                          setRatings(prev => ({
                            ...prev,
                            [productId]: { 
                              ...prev[productId], 
                              submitted: false 
                            }
                          }));
                        }}
                      >
                        <Feather name="edit-2" size={14} color="#000" />
                        <Text style={s.editButtonText}>Edit</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <TouchableOpacity
                    style={[s.submitRatingBtn, (!ratings[productId]?.rating || saving) && s.disabled]}
                    disabled={!ratings[productId]?.rating || saving}
                    onPress={() => submitRating(productId)}
                  >
                    {saving ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={s.submitRatingBtnText}>Submit Rating</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            );
          })}
        </View>
      )}

      {/* Payment */}
      {/*<View style={s.card}>
        <Text style={s.sectionTitle}>Payment</Text>
        <View style={s.rowBetween}>
          <Text style={s.muted}>Method</Text>
          <Text style={s.value}>{order.payment_method || "Card"}</Text>
        </View>
        <View style={s.rowBetween}>
          <Text style={s.muted}>Status</Text>
          <Text style={s.value}>
            {order.payment_status ? order.payment_status : "—"}
          </Text>
        </View>
        {!!order.payer_email && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Payer</Text>
            <Text style={s.value}>{order.payer_email}</Text>
          </View>
        )}
        {!!order.payment_intent_id && (
          <View style={s.rowBetween}>
            <Text style={s.muted}>Payment ID</Text>
            <Text style={s.value}>
              {String(order.payment_intent_id).slice(0, 10)}…
            </Text>
          </View>
        )}
      </View>*/}

      {/* Delivery Tracking */}
      {/* {order.delivery_id &&
        ["pickup_pending", "in_transit"].includes(
          order.delivery_status || order.status || ""
        ) && (
          <View style={s.card}>
            <Text style={s.sectionTitle}>Delivery Tracking</Text>
            <DeliveryTracker deliveryId={order.delivery_id} />
          </View>
        )} */}

      {/* Actions */}
      <View style={s.card}>
        <Text style={s.sectionTitle}>Actions</Text>
        
        {
          // показываем, если есть receipt_url либо есть базовые поля для локального реципта
          (order?.receipt_url ||
            order?.payment_method ||
            order?.payment_details?.breakdown) && (
            <TouchableOpacity
              style={[s.btn, s.btnSecondary]}
              onPress={() =>
                navigation.navigate("Receipt", {
                  order,
                })
              }
            >
              <Feather name="file-text" size={18} color="#000" />
              <Text style={s.btnSecondaryText}>View Receipt</Text>
            </TouchableOpacity>
          )
        }

        {/* Message Seller button */}
        {lines.length > 0 && lines[0].vendorName && (
          <TouchableOpacity
            style={[s.btn, s.btnSecondary, !!startingChatForVendor && s.disabled]}
            disabled={!!startingChatForVendor}
            onPress={() => startChatWithVendor(lines[0].vendorId, lines[0].vendorName)}
          >
            {startingChatForVendor ? (
              <ActivityIndicator size="small" color="#000" />
            ) : (
              <Feather name="message-circle" size={18} color="#000" />
            )}
            <Text style={s.btnSecondaryText}>Message Seller</Text>
          </TouchableOpacity>
        )}

        {canTrack && (
          <TouchableOpacity
            style={[s.btn, s.btnPrimary]}
            onPress={() => {
              if (order.tracking_url) {
                setShowTrackingModal(true);
              }
            }}
          >
            <Feather name="map-pin" size={18} color="#000" />
            <Text style={s.btnPrimaryText}>Track Delivery</Text>
          </TouchableOpacity>
        )}

        {ENABLE_CANCEL_DELIVERY && canCancelDelivery && (
          <TouchableOpacity
            style={[s.btn, s.btnDanger, saving && s.disabled]}
            disabled={saving}
            onPress={cancelDelivery}
          >
            <Feather name="truck" size={18} color="#000" />
            <Text style={s.btnDangerText}>Cancel Delivery</Text>
          </TouchableOpacity>
        )}

        {ENABLE_CANCEL_DELIVERY && canCancel && (
          <TouchableOpacity
            style={[s.btn, s.btnDanger, saving && s.disabled]}
            disabled={saving}
            onPress={cancelOrder}
          >
            <Feather name="x" size={18} color="#000" />
            <Text style={s.btnDangerText}>Cancel Order</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={[s.btn, s.btnDanger]}
          onPress={() => {
            const orderId = order.id;
            platformAlert(
              "Report Seller",
              `To report an issue with this order, please email us at:\n\nsupport@jadetradeapp.com\n\nInclude your Order ID:\n${orderId}`,
              [
                { text: "Cancel", style: "cancel" },
                {
                  text: "Open Email",
                  onPress: () => {
                    const subject = encodeURIComponent(`Report Issue - Order #${String(orderId).slice(0, 8)}`);
                    const body = encodeURIComponent(
                      `Hi JadeTrade Support,\n\nI would like to report an issue with my order.\n\nOrder ID: ${orderId}\n\nPlease describe your issue below:\n\n`
                    );
                    const mailtoUrl = `mailto:support@jadetradeapp.com?subject=${subject}&body=${body}`;
                    
                    if (Platform.OS === 'web') {
                      // On web, use window.location.href for mailto links
                      window.location.href = mailtoUrl;
                    } else {
                      // On native, use Linking
                      Linking.openURL(mailtoUrl).catch(() => {
                        // Silently fail if email client unavailable - user already has the info from the alert
                      });
                    }
                  },
                },
              ]
            );
          }}
        >
          <Feather name="flag" size={18} color="#000" />
          <Text style={s.btnDangerText}>Report Seller</Text>
        </TouchableOpacity>
      </View>

      <View style={{ height: 24 }} />
    </ScrollView>

    <InAppBrowserModal
      visible={showTrackingModal}
      url={order.tracking_url || null}
      title="Delivery Tracking"
      onClose={() => setShowTrackingModal(false)}
    />
    </>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    center: { flex: 1, alignItems: "center", justifyContent: "center", gap: 8 },
    muted: { color: colors.textLight },

  headerCard: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
    gap: 6,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: { fontSize: 18, fontWeight: "800", color: colors.text },

  statusChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusChipText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#000000",
  },

  // thumbs row
  thumbRow: {
    flexDirection: "row",
    gap: 10,
    marginHorizontal: spacing.lg,
    marginTop: spacing.xs,
  },
  thumb: {
    width: 60,
    height: 60,
    borderRadius: 12,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },

  // lists
  card: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "800",
    color: colors.text,
    marginBottom: spacing.md,
  },

  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.md,
    marginVertical: 8,
  },
  itemLeft: { width: 60 },
  itemThumb: {
    width: 60,
    height: 60,
    borderRadius: 12,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  thumbPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 12,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.border,
  },
  itemName: { fontSize: 15, fontWeight: "700", color: colors.text },
  itemMeta: { fontSize: 13, color: colors.textLight, marginTop: 2 },
  vendorNameLink: { 
    fontSize: 13, 
    color: colors.primary,
    marginTop: 4,
    fontWeight: "600",
  },

  row: { flexDirection: "row", alignItems: "center" },
  rowBetween: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginVertical: 4,
  },
  value: { color: colors.text, fontWeight: "600", fontSize: 14 },
  statusText: { ...typography.body, color: colors.textLight },

  totalLabel: { fontSize: 16, color: colors.text, fontWeight: "800" },
  totalValue: { fontSize: 18, color: colors.text, fontWeight: "800" },

  feeNote: {
    fontSize: 12,
    color: colors.textLight,
    fontStyle: "italic",
  },

  btn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    height: 52,
    borderRadius: 26,
    marginTop: 12,
  },
  btnPrimary: { backgroundColor: "#00A86B" }, // jade green
  btnPrimaryText: { color: "#000", fontWeight: "700", fontSize: 15 },
  btnSecondary: {
    backgroundColor: "#00A86B", // jade green
  },
  btnSecondaryText: { color: "#000", fontWeight: "700", fontSize: 15 },
  btnDanger: { backgroundColor: "#F43F5E" }, // rose red
  btnDangerText: { color: "#000", fontWeight: "700", fontSize: 15 },
  disabled: { opacity: 0.6 },
  
  // Rating styles
  ratingItemContainer: {
    backgroundColor: colors.background,
    borderRadius: 16,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  ratingItemHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  ratingItemThumb: {
    width: 48,
    height: 48,
    borderRadius: 10,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  ratingItemTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: "700",
    color: colors.text,
  },
  starRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: spacing.md,
    justifyContent: 'center',
  },
  feedbackInput: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 12,
    padding: spacing.md,
    fontSize: 14,
    color: colors.text,
    minHeight: 80,
    textAlignVertical: "top",
    marginBottom: spacing.md,
  },
  submitRatingBtn: {
    backgroundColor: colors.primary,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  submitRatingBtnText: {
    color: "#000",
    fontWeight: "700",
    fontSize: 14,
  },
  submittedRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: spacing.xs,
  },
  submittedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  submittedText: {
    color: colors.primary,
    fontWeight: "700",
    fontSize: 14,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    backgroundColor: colors.primary,
  },
  editButtonText: {
    color: "#000",
    fontSize: 13,
    fontWeight: "700",
  },
  submittedFeedback: {
    color: colors.textSecondary,
    fontSize: 14,
    fontStyle: "italic",
    marginBottom: spacing.sm,
    lineHeight: 20,
    paddingHorizontal: spacing.xs,
  },
  });
